/**
 * @file InternalCodeField.tsx
 * @description «Код склада» фасовки (phase19.22, W17-1, ui-design §5):
 * `CHB000000123-7` моноширинным + кнопка «копировать».
 *
 * Код выдаёт база при создании фасовки и не меняется. Показываем с дефисом
 * перед контрольной цифрой (`formatInternalCode`), в буфер кладём код без
 * дефиса — ровно то, что закодировано в штрихкоде (пикеры склада понимают
 * оба написания). У новой фасовки — «Код присвоится после сохранения».
 */

import { Copy } from "lucide-react";

import { Button } from "../../ui/button";
import { useNotify } from "../../ui/NotifyProvider";
import { formatInternalCode } from "../../../lib/product-kind-format";
import { cn } from "@/lib/utils";

interface Props {
  /** Код фасовки; пусто — ещё не присвоен. */
  code: string | null | undefined;
  /** Фасовка ещё не сохранена. */
  isNew?: boolean;
  className?: string;
}

export function InternalCodeField({ code, isNew = false, className }: Props) {
  const notify = useNotify();
  const shown = formatInternalCode(code);

  async function copy() {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code.trim().toUpperCase());
      notify.success(`Код склада ${shown} скопирован`);
    } catch {
      // Буфер недоступен (http без TLS, запрет браузера) — код виден, его
      // можно выделить руками.
      notify.error("Не удалось скопировать — выделите код вручную");
    }
  }

  return (
    <div className={cn("flex min-h-12 items-center gap-2", className)}>
      <span className="shrink-0 text-xs text-on-surface-variant">Код склада</span>
      {shown ? (
        <>
          <span className="min-w-0 truncate font-mono text-sm text-on-surface select-all tabular-nums">
            {shown}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-12 shrink-0"
            onClick={() => void copy()}
            aria-label={`Скопировать код склада ${shown}`}
          >
            <Copy className="size-4" />
          </Button>
        </>
      ) : (
        <span className="text-sm text-on-surface-variant">
          {isNew ? "Код присвоится после сохранения" : "Код склада ещё не присвоен"}
        </span>
      )}
    </div>
  );
}
